import { useState, useEffect, useRef, useMemo } from "react";

// ============================================================================
//  RevisionDataEntry — Pantalla de carga manual (data entry).
//
//  El operador ve la foto del recorte (que esta en Drive), tipea lo que
//  interpreta, y avanza. Va acumulando correcciones en memoria y las manda
//  todas juntas en un lote (PUT /api/revision/guardar-lote), para no pegarle
//  a la base en cada campo.
//
//  Backend:
//    GET  /api/revision/pendientes   -> [{ encuestaId, plantillaId, paginaIndice,
//                                          fechaProcesamiento, camposACompletar:[
//                                            { id, tipo, valor(=DriveFileId),
//                                              etiqueta, pregunta, necesitaRevision }]}]
//    PUT  /api/revision/guardar-lote -> [{ encuestaId,
//                                          camposCorregidos:[{ campoId, valorTipeado }]}]
//
//  Uso: <RevisionDataEntry baseApi="http://localhost:5132" />
// ============================================================================

const TEAL = "#286076";
const TEAL_BRILLANTE = "#19A0AF";
const TINTA = "#1a2b31";

// URL de la imagen en Drive a partir del DriveFileId
const urlDrive = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w600`;

export default function RevisionDataEntry({ baseApi = "https://pideapi2025.azurewebsites.net" }) {
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  // lista plana de items a revisar: { encuestaId, campoId, pregunta, driveFileId }
  const [items, setItems] = useState([]);
  const [indice, setIndice] = useState(0);
  // valores tipeados: clave `${encuestaId}:${campoId}` -> texto
  const [valores, setValores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const inputRef = useRef(null);

  // ---- cargar los pendientes ----
  const cargarPendientes = async () => {
    setCargando(true);
    setError("");
    setMensajeOk("");
    try {
      const res = await fetch(`${baseApi}/api/revision/pendientes`);
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
      const encuestas = await res.json();

      // aplanar: un item por cada campo de texto a revisar
      const planos = [];
      for (const enc of encuestas) {
        for (const campo of enc.camposACompletar || []) {
          planos.push({
            encuestaId: enc.encuestaId,
            campoId: campo.id,
            pregunta: campo.pregunta || campo.id,
            driveFileId: campo.valor,   // el DriveFileId viene en 'valor'
            paginaIndice: enc.paginaIndice,
          });
        }
      }
      setItems(planos);
      setIndice(0);
      setValores({});
    } catch (err) {
      setError(err.message || "No se pudieron cargar los pendientes.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarPendientes(); }, []);

  // foco automatico al cambiar de item
  useEffect(() => {
    if (!cargando && items.length > 0) inputRef.current?.focus();
  }, [indice, cargando, items.length]);

  const itemActual = items[indice];
  const claveActual = itemActual ? `${itemActual.encuestaId}:${itemActual.campoId}` : "";
  const valorActual = valores[claveActual] ?? "";

  // cuantos llevo cargados (con texto no vacio)
  const cargados = useMemo(
    () => Object.values(valores).filter((v) => v.trim() !== "").length,
    [valores]
  );

  const setValorActual = (texto) =>
    setValores((prev) => ({ ...prev, [claveActual]: texto }));

  const avanzar = () => {
    if (indice < items.length - 1) setIndice(indice + 1);
  };
  const retroceder = () => {
    if (indice > 0) setIndice(indice - 1);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); avanzar(); }
  };

  // ---- enviar el lote ----
  const enviarLote = async () => {
    // agrupar por encuesta los campos que tienen texto cargado
    const porEncuesta = {};
    for (const it of items) {
      const clave = `${it.encuestaId}:${it.campoId}`;
      const texto = (valores[clave] ?? "").trim();
      if (texto === "") continue;   // saltear los que no cargó
      (porEncuesta[it.encuestaId] ||= []).push({
        campoId: it.campoId, valorTipeado: texto,
      });
    }
    const lote = Object.entries(porEncuesta).map(([encuestaId, camposCorregidos]) => ({
      encuestaId, camposCorregidos,
    }));

    if (lote.length === 0) {
      setError("No cargaste ningún campo todavía.");
      return;
    }

    setEnviando(true);
    setError("");
    try {
      const res = await fetch(`${baseApi}/api/revision/guardar-lote`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(lote),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`El servidor respondió ${res.status}. ${t}`);
      }
      const data = await res.json();
      setMensajeOk(data.mensaje || "Lote guardado.");
      // recargar los pendientes (los recién validados ya no aparecen)
      await cargarPendientes();
    } catch (err) {
      setError(err.message || "No se pudo guardar el lote.");
    } finally {
      setEnviando(false);
    }
  };

  // ---- estados de pantalla ----
  if (cargando) {
    return <Marco><p style={S.estado}>Cargando pendientes…</p></Marco>;
  }

  if (error && items.length === 0) {
    return (
      <Marco>
        <p style={S.estadoError}>{error}</p>
        <button style={S.btnSec} onClick={cargarPendientes}>Reintentar</button>
      </Marco>
    );
  }

  if (items.length === 0) {
    return (
      <Marco>
        <div style={S.vacio}>
          <div style={S.vacioIcono}>✓</div>
          <h2 style={S.vacioTit}>No hay nada para revisar</h2>
          <p style={S.vacioSub}>
            {mensajeOk || "Todas las respuestas escaneadas están validadas."}
          </p>
          <button style={S.btnSec} onClick={cargarPendientes}>Buscar de nuevo</button>
        </div>
      </Marco>
    );
  }

  return (
    <Marco>
      {/* barra superior: progreso + contador de cargados */}
      <div style={S.topbar}>
        <div style={S.marca}>DESEM · Revisión</div>
        <div style={S.progreso}>
          Campo {indice + 1} de {items.length}
          <span style={S.contador}>{cargados} cargados</span>
        </div>
      </div>

      {/* la foto del recorte */}
      <div style={S.zonaImagen}>
        <div style={S.preguntaLbl}>{itemActual.pregunta}</div>
        <div style={S.imagenMarco}>
          <img
            key={itemActual.driveFileId}
            src={urlDrive(itemActual.driveFileId)}
            alt={`Recorte de ${itemActual.pregunta}`}
            style={S.imagen}
            onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
          />
        </div>
        <p style={S.ayuda}>Escribí lo que ves en la imagen y presioná Enter.</p>
      </div>

      {/* campo de transcripcion */}
      <div style={S.zonaInput}>
        <input
          ref={inputRef}
          style={S.input}
          value={valorActual}
          placeholder="Transcribí acá…"
          onChange={(e) => setValorActual(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        <div style={S.navBotones}>
          <button style={S.btnNav} onClick={retroceder} disabled={indice === 0}>← Anterior</button>
          <button style={S.btnNav} onClick={avanzar} disabled={indice === items.length - 1}>Siguiente →</button>
        </div>
      </div>

      {/* pie: enviar lote */}
      <div style={S.pie}>
        {error && <div style={S.errorInline}>{error}</div>}
        <button
          style={{ ...S.btnEnviar, opacity: enviando || cargados === 0 ? 0.5 : 1 }}
          disabled={enviando || cargados === 0}
          onClick={enviarLote}
        >
          {enviando ? "Guardando…" : `Guardar ${cargados} ${cargados === 1 ? "carga" : "cargas"}`}
        </button>
      </div>
    </Marco>
  );
}

function Marco({ children }) {
  return (
    <div style={S.pagina}>
      <div style={S.tarjeta}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  estilos
// ---------------------------------------------------------------------------
const S = {
  pagina: {
    minHeight: "100vh", background: "#E7E9E6", display: "flex",
    alignItems: "center", justifyContent: "center", padding: 20,
    fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif", color: TINTA,
  },
  tarjeta: {
    width: "100%", maxWidth: 460, background: "#fff", borderRadius: 18,
    boxShadow: "0 8px 40px rgba(0,0,0,.12)", overflow: "hidden",
    display: "flex", flexDirection: "column",
  },
  topbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", borderBottom: "1px solid #eef1ef",
  },
  marca: { fontSize: 12, fontWeight: 700, color: TEAL, letterSpacing: ".04em", textTransform: "uppercase" },
  progreso: { fontSize: 13, color: "#6b706e", display: "flex", alignItems: "center", gap: 10 },
  contador: {
    fontSize: 12, fontWeight: 700, color: TEAL_BRILLANTE, background: "#E1F0F2",
    padding: "3px 9px", borderRadius: 20,
  },

  zonaImagen: { padding: "22px 20px 8px" },
  preguntaLbl: { fontSize: 20, fontWeight: 700, marginBottom: 14, letterSpacing: "-.01em" },
  imagenMarco: {
    background: "#F4F5F3", border: "1px solid #e2e6e3", borderRadius: 12,
    padding: 10, display: "flex", justifyContent: "center", alignItems: "center",
    minHeight: 120,
  },
  imagen: { maxWidth: "100%", maxHeight: 200, borderRadius: 6, display: "block" },
  ayuda: { fontSize: 12, color: "#a0a5a2", marginTop: 10, marginBottom: 0 },

  zonaInput: { padding: "8px 20px 4px" },
  input: {
    width: "100%", padding: "14px 16px", fontSize: 20, fontWeight: 600,
    border: `2px solid ${TEAL}`, borderRadius: 12, outline: "none",
    boxSizing: "border-box", color: TINTA,
  },
  navBotones: { display: "flex", justifyContent: "space-between", marginTop: 12, gap: 10 },
  btnNav: {
    flex: 1, padding: "9px", background: "#f0f2f0", border: "none", borderRadius: 9,
    fontSize: 13, fontWeight: 600, color: "#4a4f4d", cursor: "pointer",
  },

  pie: { padding: "12px 20px 20px", marginTop: 8 },
  btnEnviar: {
    width: "100%", background: TEAL, color: "#fff", border: "none", borderRadius: 12,
    padding: "14px", fontSize: 15, fontWeight: 700, cursor: "pointer",
  },
  errorInline: {
    marginBottom: 10, padding: "8px 10px", background: "#FBEEEA",
    border: "1px solid #F0D2C8", borderRadius: 8, fontSize: 13, color: "#9a3a1a",
  },

  estado: { padding: 40, textAlign: "center", color: "#6b706e", fontSize: 14 },
  estadoError: { padding: "30px 20px 10px", textAlign: "center", color: "#9a3a1a", fontSize: 14 },
  vacio: { padding: "48px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  vacioIcono: {
    width: 56, height: 56, borderRadius: "50%", background: "#E1F5EE", color: "#0F6E56",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, marginBottom: 6,
  },
  vacioTit: { fontSize: 18, fontWeight: 700, margin: 0 },
  vacioSub: { fontSize: 14, color: "#6b706e", margin: "0 0 12px", lineHeight: 1.5 },
  btnSec: {
    background: "#fff", color: TEAL, border: `1.5px solid ${TEAL}`, borderRadius: 10,
    padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
};